import { BlockStack, Box, Card, Divider, Page, Text, Banner, TextField, Button, InlineStack, DataTable, Modal, Select, Spinner } from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import ShippingPricingTable from "../components/ShippingPricingTable";
import { useState, useEffect, useCallback } from "react";
import apiClient from "../utils/api";
import { useMarketplacesList } from "../utils/queries";

export const ShippingAndReturn = () => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Fetch marketplaces dynamically
    const { data: marketplacesData, isLoading: marketplacesLoading } = useMarketplacesList();

    // Global return fee state
    const [globalReturnFee, setGlobalReturnFee] = useState('0.00');
    const [hasGlobalDefault, setHasGlobalDefault] = useState(false);
    const [marketplaceOverrides, setMarketplaceOverrides] = useState({});
    const [returnFeeLoading, setReturnFeeLoading] = useState(true);
    const [returnFeeSaving, setReturnFeeSaving] = useState(false);

    // Modal state for adding marketplace override
    const [showAddMarketplaceModal, setShowAddMarketplaceModal] = useState(false);
    const [newMarketplace, setNewMarketplace] = useState('');
    const [newMarketplaceFee, setNewMarketplaceFee] = useState('0.00');

    // Load global return fee defaults
    const loadGlobalReturnFees = useCallback(async () => {
        try {
            setReturnFeeLoading(true);
            const response = await apiClient.getGlobalReturnFeeDefaults();
            if (response.success) {
                setGlobalReturnFee(response.defaultReturnFee?.toFixed(2) || '0.00');
                setHasGlobalDefault(response.hasGlobalDefault || false);
                setMarketplaceOverrides(response.marketplaceOverrides || {});
            }
        } catch (err) {
            console.error('Error loading global return fees:', err);
            setError('Failed to load global return fee defaults');
        } finally {
            setReturnFeeLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGlobalReturnFees();
    }, [loadGlobalReturnFees]);

    // Save global default return fee
    const saveGlobalReturnFee = async () => {
        try {
            setReturnFeeSaving(true);
            setError('');
            const fee = parseFloat(globalReturnFee);
            if (isNaN(fee) || fee < 0) {
                setError('Return fee must be a non-negative number');
                return;
            }
            const response = await apiClient.updateGlobalReturnFeeDefault(fee);
            if (response.success) {
                setHasGlobalDefault(true);
                await loadGlobalReturnFees();
            }
        } catch (err) {
            console.error('Error saving global return fee:', err);
            setError('Failed to save global return fee');
        } finally {
            setReturnFeeSaving(false);
        }
    };

    // Add marketplace override
    const addMarketplaceOverride = async () => {
        if (!newMarketplace) {
            setError('Please select a marketplace');
            return;
        }
        try {
            setReturnFeeSaving(true);
            setError('');
            const fee = parseFloat(newMarketplaceFee);
            if (isNaN(fee) || fee < 0) {
                setError('Return fee must be a non-negative number');
                return;
            }
            const response = await apiClient.createGlobalMarketplaceReturnFee(newMarketplace, fee);
            if (response.success) {
                setShowAddMarketplaceModal(false);
                setNewMarketplace('');
                setNewMarketplaceFee('0.00');
                await loadGlobalReturnFees();
            }
        } catch (err) {
            console.error('Error adding marketplace override:', err);
            setError(err.message || 'Failed to add marketplace override');
        } finally {
            setReturnFeeSaving(false);
        }
    };

    // Delete marketplace override
    const deleteMarketplaceOverride = async (marketplace) => {
        if (!confirm(`Are you sure you want to delete the return fee override for "${marketplace}"?`)) {
            return;
        }
        try {
            setReturnFeeSaving(true);
            setError('');
            const response = await apiClient.deleteGlobalMarketplaceReturnFee(marketplace);
            if (response.success) {
                await loadGlobalReturnFees();
            }
        } catch (err) {
            console.error('Error deleting marketplace override:', err);
            setError('Failed to delete marketplace override');
        } finally {
            setReturnFeeSaving(false);
        }
    };

    // Build marketplace override rows for DataTable
    const marketplaceRows = Object.entries(marketplaceOverrides).map(([marketplace, fee]) => [
        marketplace,
        `$${fee.toFixed(2)}`,
        <Button
            key={marketplace}
            icon={DeleteIcon}
            tone="critical"
            size="micro"
            onClick={() => deleteMarketplaceOverride(marketplace)}
            disabled={returnFeeSaving}
        >
            Remove
        </Button>
    ]);

    // Build marketplace options dynamically from API, filter out already-configured ones
    const availableMarketplaceOptions = [
        { label: 'Select marketplace...', value: '' },
        ...(marketplacesData?.marketplaces || [])
            .filter(mp => !marketplaceOverrides[mp])
            .map(mp => ({ label: mp, value: mp }))
    ];

    const handleDefaultShippingPricingSave = async (changes, fullPricingData) => {
        if (!changes || changes.length === 0) return;

        try {
            setSaving(true);
            setError('');

            // Transform changes into the format expected by the PATCH API.
            // weightMin/weightMax come from the change itself - ShippingPricingTable resolves
            // them against the bands it actually rendered (which include bands read from the
            // database), so there is no local copy of the weight ladder to drift out of sync.
            const updates = changes.map(change => ({
                shippingMethod: change.serviceId,
                weightMin: change.weightMin,
                weightMax: change.weightMax,
                fee: change.newValue ? parseFloat(change.newValue) : null, // null = delete/reset
                oldFee: change.oldValue ? parseFloat(change.oldValue) : null // for audit
            }));

            // Use PATCH endpoint for granular updates
            const response = await apiClient.request(`/brands/default/shipping-fees`, {
                method: 'PATCH',
                body: JSON.stringify({ updates }),
            });

            if (!response.success) {
                throw new Error(response.message || 'Failed to save shipping pricing');
            }
        } catch (err) {
            console.error('Error saving shipping pricing:', err);
            setError(`Failed to save shipping pricing: ${err.message}`);
            throw err; // Re-throw so the component can handle the error
        } finally {
            setSaving(false);
        }
    };

    return (
        <Page
            title="Shipping & Return"
            subtitle="Manage the default shipping and return rates applied across all brands."
            fullWidth
        >
            <BlockStack gap="400">
                {/* Error banner */}
                {error && (
                    <Banner tone="critical" onDismiss={() => setError('')}>
                        {error}
                    </Banner>
                )}

                {/* Shipping Pricing Section */}
                <Card>
                    <Box padding="200">
                        <BlockStack gap="300">
                            <BlockStack gap="050">
                                <Text variant="headingMd" as="h3">BoxC Shipping Pricing</Text>
                                <Text variant="bodyMd" tone="subdued">
                                    Configure default pricing for BoxC services across different weight ranges. These rates apply to all brands unless overridden.
                                </Text>
                            </BlockStack>
                            {/* No `disabled` prop: this screen has no sync, and the table
                                already blocks editing while `loading` (a save) is in flight.
                                An `isSyncing` state existed here whose setter was never
                                called, so `disabled` was permanently false. */}
                            <ShippingPricingTable
                                brand={null}
                                onSave={handleDefaultShippingPricingSave}
                                loading={saving}
                            />
                        </BlockStack>
                    </Box>
                </Card>

                {/* Return Fees Section */}
                <Card>
                    <Box padding="200">
                        <BlockStack gap="300">
                            <BlockStack gap="050">
                                <Text variant="headingMd" as="h3">Global Return Fee Defaults</Text>
                                <Text variant="bodyMd" tone="subdued">
                                    Configure default return fees applied when processing refunds. Brands can override these values with their own settings.
                                </Text>
                            </BlockStack>

                            {/* Sections divided, not nested cards.

                                These two were Cards on a tinted background inside another Card,
                                which reads as a card boundary carrying information it does not
                                have: they are the two halves of "Global Return Fee Defaults" —
                                the default, and the per-marketplace exceptions to it. Same
                                treatment as the brand tab.

                                The field's own label is hidden: the section heading directly
                                above it already says "Default Return Fee", so "Fee amount" was
                                a second label for one input. */}
                            <Divider />

                            <BlockStack gap="300">
                                        <Text variant="headingSm" as="h4">Default Return Fee</Text>
                                        <InlineStack gap="300" align="start" blockAlign="end">
                                            <div style={{ width: '200px' }}>
                                                <TextField
                                                    label="Default return fee"
                                                    labelHidden
                                                    type="number"
                                                    prefix="$"
                                                    value={globalReturnFee}
                                                    onChange={setGlobalReturnFee}
                                                    min={0}
                                                    step={0.01}
                                                    disabled={returnFeeLoading || returnFeeSaving}
                                                    autoComplete="off"
                                                />
                                            </div>
                                            <Button
                                                variant="primary"
                                                onClick={saveGlobalReturnFee}
                                                loading={returnFeeSaving}
                                                disabled={returnFeeLoading}
                                            >
                                                {hasGlobalDefault ? 'Update' : 'Set'}
                                            </Button>
                                        </InlineStack>
                                        {hasGlobalDefault && (
                                            <Text variant="bodySm" tone="subdued">
                                                Current default: ${parseFloat(globalReturnFee).toFixed(2)} - This applies to all brands unless they have their own override.
                                            </Text>
                                        )}
                            </BlockStack>

                            <Divider />

                            <BlockStack gap="300">
                                        {/* The heading row and its description share an inner
                                            050 stack. On the outer 300 the description sat 12px
                                            below its own heading — as far from it as the table
                                            is — so it read as a standalone sentence rather than
                                            as the subtitle of the heading above it. The outer
                                            gap still separates the pair from the table. */}
                                        <BlockStack gap="050">
                                            <InlineStack align="space-between" blockAlign="center">
                                                <Text variant="headingSm" as="h4">Marketplace Overrides</Text>
                                                <Button
                                                    onClick={() => setShowAddMarketplaceModal(true)}
                                                    disabled={returnFeeSaving || marketplacesLoading || availableMarketplaceOptions.length <= 1}
                                                    loading={marketplacesLoading}
                                                >
                                                    Add
                                                </Button>
                                            </InlineStack>
                                            <Text variant="bodySm" tone="subdued">
                                                Set different return fees for specific marketplaces. These override the default return fee globally.
                                                {!marketplacesLoading && availableMarketplaceOptions.length <= 1 && Object.keys(marketplaceOverrides).length > 0 && (
                                                    <> All marketplaces have been configured.</>
                                                )}
                                            </Text>
                                        </BlockStack>

                                        {marketplaceRows.length > 0 ? (
                                            <DataTable
                                                columnContentTypes={['text', 'text', 'text']}
                                                headings={['Marketplace', 'Return Fee', 'Actions'].map((h) => (
                                                    <Text key={h} variant="bodyMd" fontWeight="semibold" as="span">{h}</Text>
                                                ))}
                                                rows={marketplaceRows}
                                            />
                                        ) : (
                                            <Box paddingBlock="400">
                                                <Text tone="subdued" alignment="center">
                                                    No marketplace overrides configured. All marketplaces use the default return fee
                                                    {hasGlobalDefault ? ` of $${parseFloat(globalReturnFee).toFixed(2)}` : ''}.
                                                </Text>
                                            </Box>
                                        )}
                            </BlockStack>
                        </BlockStack>
                    </Box>
                </Card>
            </BlockStack>

            {/* Add Marketplace Override Modal */}
            <Modal
                open={showAddMarketplaceModal}
                onClose={() => {
                    setShowAddMarketplaceModal(false);
                    setNewMarketplace('');
                    setNewMarketplaceFee('0.00');
                }}
                title="Add Marketplace Return Fee Override"
                primaryAction={{
                    content: 'Add Override',
                    onAction: addMarketplaceOverride,
                    loading: returnFeeSaving,
                    disabled: !newMarketplace,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => {
                            setShowAddMarketplaceModal(false);
                            setNewMarketplace('');
                            setNewMarketplaceFee('0.00');
                        },
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <Select
                            label="Marketplace"
                            options={availableMarketplaceOptions}
                            value={newMarketplace}
                            onChange={setNewMarketplace}
                            disabled={marketplacesLoading}
                            helpText={marketplacesLoading ? "Loading marketplaces..." : undefined}
                        />
                        <TextField
                            label="Return Fee"
                            type="number"
                            prefix="$"
                            value={newMarketplaceFee}
                            onChange={setNewMarketplaceFee}
                            min={0}
                            step={0.01}
                            helpText={hasGlobalDefault ? `Current global default: $${parseFloat(globalReturnFee).toFixed(2)}` : 'No global default set'}
                            autoComplete="off"
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </Page>
    )
}